import { Search, X, SlidersHorizontal } from "lucide-react";
import { motion } from "framer-motion";

const SORT_OPTIONS = [
  { value: "recent", label: "Most recent" },
  { value: "price_asc", label: "Price · low → high" },
  { value: "price_desc", label: "Price · high → low" },
];

/**
 * Marketplace search + filter sidebar.
 * Pure presentational — owner page holds state and re-fetches on change.
 */
export default function MarketplaceFilters({
  query, onQueryChange,
  category, onCategoryChange,
  minPrice, onMinPriceChange,
  maxPrice, onMaxPriceChange,
  sort, onSortChange,
  facets,
  resultCount,
  onClear,
}) {
  const totalCategories = facets?.categories || [];
  const priceRange = facets?.price_range || { min: 0, max: 0 };
  const hasActive = !!(query || category || minPrice || maxPrice || sort !== "recent");

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-2xl p-4 space-y-4"
      data-testid="marketplace-filters"
    >
      <div className="flex items-center gap-2 text-[#64748B]">
        <SlidersHorizontal size={14} />
        <div className="text-[10px] uppercase tracking-[0.25em]">Refine</div>
      </div>

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <input
          data-testid="marketplace-search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search products…"
          className="nexus-input !pl-9"
          autoComplete="off"
        />
      </div>

      <div className="space-y-1.5">
        <div className="text-[11px] text-[#64748B] uppercase tracking-wider">Category</div>
        <div className="flex flex-wrap gap-1.5" data-testid="marketplace-categories">
          <Chip
            active={!category || category === "all"}
            onClick={() => onCategoryChange("all")}
            label={`All · ${facets?.total || 0}`}
            testid="cat-all"
          />
          {totalCategories.map((c) => (
            <Chip
              key={c.category}
              active={category === c.category}
              onClick={() => onCategoryChange(c.category)}
              label={`${c.category} · ${c.count}`}
              testid={`cat-${c.category}`}
            />
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="text-[11px] text-[#64748B] uppercase tracking-wider">
          Price <span className="text-[#94A3B8] normal-case">
            (${priceRange.min.toFixed(0)}–${priceRange.max.toFixed(0)})
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input
            data-testid="marketplace-min-price"
            type="number"
            min="0"
            step="1"
            value={minPrice}
            onChange={(e) => onMinPriceChange(e.target.value)}
            placeholder="Min"
            className="nexus-input"
          />
          <input
            data-testid="marketplace-max-price"
            type="number"
            min="0"
            step="1"
            value={maxPrice}
            onChange={(e) => onMaxPriceChange(e.target.value)}
            placeholder="Max"
            className="nexus-input"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="text-[11px] text-[#64748B] uppercase tracking-wider">Sort</div>
        <select
          data-testid="marketplace-sort"
          value={sort}
          onChange={(e) => onSortChange(e.target.value)}
          className="nexus-input"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center justify-between pt-1 border-t border-slate-100">
        <div className="text-xs text-[#64748B]" data-testid="marketplace-result-count">
          {resultCount} result{resultCount === 1 ? "" : "s"}
        </div>
        {hasActive && (
          <button
            data-testid="marketplace-clear-filters"
            onClick={onClear}
            className="text-xs text-[#00E5FF] hover:underline flex items-center gap-1"
          >
            <X size={12} /> Clear
          </button>
        )}
      </div>
    </motion.div>
  );
}

function Chip({ active, onClick, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all border ${
        active
          ? "bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/40"
          : "bg-slate-50 text-[#64748B] border-slate-100 hover:border-slate-300"
      }`}
    >
      {label}
    </button>
  );
}
